import clsx from 'clsx'
import { TrendingUp, TrendingDown, Minus, ArrowUp, ArrowDown } from 'lucide-react'

interface TrendIndicatorProps {
  direction: 'up' | 'down' | 'stable'
  value?: number
  label?: string
  variant?: 'default' | 'compact' | 'detailed'
  positiveIsGood?: boolean
  className?: string
}

export default function TrendIndicator({
  direction,
  value,
  label,
  variant = 'default',
  positiveIsGood = true,
  className
}: TrendIndicatorProps) {
  // Determine color based on direction and whether positive is good
  const getColor = () => {
    if (direction === 'stable') return 'text-surface-400'
    if (direction === 'up') {
      return positiveIsGood ? 'text-status-healthy' : 'text-status-critical'
    }
    return positiveIsGood ? 'text-status-critical' : 'text-status-healthy'
  }

  const getBgColor = () => {
    if (direction === 'stable') return 'bg-surface-700/50'
    if (direction === 'up') {
      return positiveIsGood ? 'bg-status-healthy/10' : 'bg-status-critical/10'
    }
    return positiveIsGood ? 'bg-status-critical/10' : 'bg-status-healthy/10'
  }

  const IconComponent = direction === 'up'
    ? TrendingUp
    : direction === 'down'
      ? TrendingDown
      : Minus

  const ArrowComponent = direction === 'up'
    ? ArrowUp
    : direction === 'down'
      ? ArrowDown
      : Minus

  if (variant === 'compact') {
    return (
      <span className={clsx('inline-flex items-center gap-0.5', getColor(), className)}>
        <ArrowComponent size={12} />
        {value !== undefined && (
          <span className="text-xs font-medium">{Math.abs(value)}%</span>
        )}
      </span>
    )
  }

  if (variant === 'detailed') {
    return (
      <div className={clsx('flex items-center gap-2 px-2 py-1 rounded-md', getBgColor(), className)}>
        <IconComponent size={16} className={getColor()} />
        <div className="flex flex-col">
          {value !== undefined && (
            <span className={clsx('text-sm font-semibold', getColor())}>
              {direction === 'up' ? '+' : direction === 'down' ? '-' : ''}
              {Math.abs(value)}%
            </span>
          )}
          {label && (
            <span className="text-xs text-surface-500">{label}</span>
          )}
        </div>
      </div>
    )
  }

  // Default variant
  return (
    <span className={clsx('inline-flex items-center gap-1', getColor(), className)}>
      <IconComponent size={14} />
      {value !== undefined && (
        <span className="text-xs font-medium">
          {direction === 'up' ? '+' : direction === 'down' ? '-' : ''}
          {Math.abs(value)}%
        </span>
      )}
      {label && (
        <span className="text-xs text-surface-500 ml-1">{label}</span>
      )}
    </span>
  )
}
